import { useDispatch, useSelector } from 'react-redux';

import { Box, PresentationalRadioGroup } from '@opendoor/bricks/core';

import type { FlowQuestion, RadioGroupQuestion } from 'src/lib/flow/FlowQuestion';
import { getAnswers } from 'src/store/sellerFlow/selectors';
import { changeAnswers, SavedAnswer } from 'src/store/sellerInput';

import QuestionLabel from '../common/QuestionLabel';
import Fieldset from './Fieldset';
import { getOptions } from './getOptions';
import { trackAnswerChange } from './trackAnswerChange';

/**
 * Given an array of questions, return all the keys of those questions and any
 * follow up questions
 */
export const getAllKeys = (questions: FlowQuestion[]): Array<string> => {
  const keys: Array<string> = [];
  questions.forEach((question) => {
    keys.push(question.key);
    if ('options' in question && question.options) {
      question.options.forEach((option) => {
        if ('followUps' in option && Array.isArray(option.followUps)) {
          keys.push(...getAllKeys(option.followUps));
        }
      });
    }
  });
  return keys;
};

// exported for testing
export const getKeysToClear = (
  options: (FlowQuestion & { type: 'radio_single_select' })['options'],
  newValue: string | boolean | number,
) => {
  const keys: Array<string> = [];
  (options ?? []).forEach((option) => {
    if (option.followUps && newValue !== option.value) {
      // get keys to remove
      keys.push(...getAllKeys(option.followUps));
    }
  });
  return keys;
};

type RadioGroupProps = {
  fieldsetLegend: string;
  label?: string;
  subLabel?: string;
  tooltip?: string;
  name: string;
  value?: string;
  options: RadioGroupQuestion['options'];
  isFollowUp: boolean;
  hasError: boolean;
  pageHasError: boolean;
};

const RadioGroup: React.FC<RadioGroupProps> = ({
  fieldsetLegend,
  label,
  subLabel,
  tooltip,
  name,
  value,
  options,
  isFollowUp,
  hasError,
  pageHasError,
}) => {
  const dispatch = useDispatch();
  const answers = useSelector(getAnswers);

  const onChange = (name: string, newValue: string | boolean) => {
    const keys = getKeysToClear(options, newValue);

    const newAnswers: Array<{ key: string; answer: SavedAnswer | null }> = [];
    //TODO: replace with a clearAnswers action?
    keys.forEach((key) => {
      newAnswers.push({ key, answer: null });
    });

    newAnswers.push({ key: name, answer: newValue });

    dispatch(changeAnswers(newAnswers));
    trackAnswerChange(name, JSON.stringify(newValue));
  };

  const onClick = (value: string) => {
    // If the selected value is string 'true' or 'false',
    // cast and store as boolean
    let valueToSet: string | boolean = value;
    if (value === 'true') {
      valueToSet = true;
    } else if (value === 'false') {
      valueToSet = false;
    }
    onChange(name, valueToSet);
  };

  const radioOptions = getOptions(
    options,
    answers,
    onChange,
    !!hasError,
    !!pageHasError,
    !!isFollowUp,
    value ? [value] : undefined,
    false,
  );

  //TODO: {...followUpStyles} - and can this box be not here?
  return (
    <Box maxWidth={['325px', '450px']} width={['100%', '450px']}>
      <QuestionLabel title={label} tooltip={tooltip} subtitle={subLabel} unboundedWidth />
      <Fieldset questionTitle={fieldsetLegend || label || ''}>
        <PresentationalRadioGroup
          role="none"
          onClick={onClick}
          hasContainer={!isFollowUp}
          options={radioOptions}
          selectedValue={`${value}`}
          name={name}
          analyticsName={`reception-fe-question-${name}`}
        />
      </Fieldset>
    </Box>
  );
};

export default RadioGroup;
